/// <amd-module name="Wiki/Editor/FileSource" />

import { MAX_ATTACHMENT_FILE_SIZE } from "Wiki/Editor/AttachmentHelpers";
import { Attachment, GuidSuffixedFile } from "Wiki/ViewCommon/Contracts";

export class FileSource {
    public static createAttachment(file: GuidSuffixedFile): Attachment {
        const attachment = { file: file } as Attachment;
        if (file.size > MAX_ATTACHMENT_FILE_SIZE) {
            attachment.error = new Error("Exceeded maximum allowed size limit.");
        }

        try {
            if (file.type === "image/svg+xml") {
                attachment.blob = new Blob([file], { type: file.type });
            } else {
                attachment.blob = new Blob([file]);
            }

            attachment.objectUrl = URL.createObjectURL(attachment.blob);
        } catch (e) {
            attachment.error = e;
        }

        return attachment;
    }

    // passing optional fileReader for UTs
    public readContent(attachment: Attachment, fileReader?: FileReader): Promise<Attachment> {
        if (attachment.error) {
            return Promise.resolve(attachment);
        }

        const reader = fileReader || new FileReader();
        return new Promise<Attachment>(resolve => {
            const onAbort = () => {
                attachment.error = new Error("Error converting blob to base64");
                resolve(attachment);
            };
            reader.onabort = onAbort;
            reader.onerror = onAbort;
            reader.onloadend = () => {
                this._onLoadEnd(attachment, reader, resolve);
            };

            try {
                reader.readAsDataURL(attachment.blob);
            } catch (e) {
                attachment.error = e;
                resolve(attachment);
            }
        });
    }

    private _onLoadEnd = (attachment: Attachment, reader: FileReader, resolve: (attachment: Attachment) => void): void => {
        const dataUrl: string = reader.result as string;
        // If the file is empty, Chrome returns result="data:" and IE returns result=null, so we check for both of these cases.
        if (dataUrl) {
            const startIndex = dataUrl.indexOf(",");
            if (startIndex >= 0) {
                let content = dataUrl.substr(startIndex + 1); // Get the base64 content portion of the data url
                // Trim a leading "//" only if it isn't part of the base64 content itself (base64 length is a multiple of 4).
                if (content.substr(0, 2) === "//" && content.length % 4 === 2) {
                    content = content.substr(2);
                }

                attachment.base64Content = content;
            }
        }

        resolve(attachment);
    };
}
